"use client";

import { c as _c } from "react/compiler-runtime";
import { classNames } from "@/lib/utils";
import { PlusIcon, Cog6ToothIcon } from "@heroicons/react/20/solid";
import MobileMenuButton from "./MobileMenuButton";
import MobileAccountIndicator from "./MobileAccountIndicator";
interface FilterTab {
  label: string;
  icon?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}
interface ActionButton {
  label: string;
  icon?: "plus" | "settings" | React.ReactNode;
  variant?: "primary" | "secondary";
  onClick?: () => void;
  /** Fully custom element - bypasses default button styling */
  customElement?: React.ReactNode;
  /** Custom element for mobile (if different from desktop) */
  customMobileElement?: React.ReactNode;
}
interface PageTitleProps {
  /** The main title text */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Filter tabs (e.g., "Overview", "Reports") */
  tabs?: FilterTab[];
  /** Action buttons on the right (e.g., "Add growth tools") */
  actions?: ActionButton[];
  /** Additional CSS classes */
  className?: string;
}

/**
 * PageTitle - Responsive page header with underline tabs
 *
 * Desktop: Title row + tabs row (underline style)
 * Mobile: Title row + scrollable tabs + full-width actions
 */
export default function PageTitle(t0) {
  const $ = _c(36);
  const {
    title,
    subtitle,
    tabs,
    actions,
    className
  } = t0;
  const renderIcon = _temp;
  const getButtonStyles = _temp2;
  const hasTabs = tabs && tabs.length > 0;
  const hasActions = actions && actions.length > 0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames("bg-white", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = subtitle ? "items-start" : "items-center";
  let t3;
  if ($[2] !== t2) {
    t3 = classNames("flex gap-3", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MobileMenuButton />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = subtitle ? "flex flex-col gap-0.5" : "";
  let t6;
  if ($[5] !== title) {
    t6 = <h1 className="text-xl font-bold leading-6 tracking-tight text-gray-900">{title}</h1>;
    $[5] = title;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== subtitle) {
    t7 = subtitle && <p className="text-[13px] text-gray-500">{subtitle}</p>;
    $[7] = subtitle;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t5 || $[10] !== t6 || $[11] !== t7) {
    t8 = <div className={t5}>{t6}{t7}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t3 || $[14] !== t8) {
    t9 = <div className={t3}>{t4}{t8}</div>;
    $[13] = t3;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <MobileAccountIndicator />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== actions || $[18] !== hasActions) {
    t11 = hasActions && <div className="hidden items-center gap-3 md:flex">{actions.map((action, index) => action.customElement ? <div key={index}>{action.customElement}</div> : <button key={index} onClick={action.onClick} className={getButtonStyles(action.variant)}>{action.icon && renderIcon(action.icon)}<span>{action.label}</span></button>)}</div>;
    $[17] = actions;
    $[18] = hasActions;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = <div className="flex items-center gap-3">{t10}{t11}</div>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t9) {
    t13 = <div className="flex items-center justify-between py-3">{t9}{t12}</div>;
    $[22] = t12;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== hasTabs || $[26] !== tabs) {
    t14 = hasTabs && <div className="border-b border-gray-200/60"><nav className="no-scrollbar scroll-fade-x -mb-px flex gap-1 overflow-x-auto">{tabs.map(_temp3)}</nav></div>;
    $[25] = hasTabs;
    $[26] = tabs;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== actions || $[29] !== hasActions) {
    t15 = hasActions && <div className="flex gap-2 border-t border-gray-100 py-3 md:hidden">{actions.map((action_0, index_1) => {
        const mobileElement = action_0.customMobileElement ?? action_0.customElement;
        if (mobileElement) {
          return <div key={index_1} className="flex-1">{mobileElement}</div>;
        }
        return <button key={index_1} onClick={action_0.onClick} className={classNames("inline-flex flex-1 items-center justify-center gap-2", "h-11 px-4 py-2", "text-sm font-semibold", "rounded-xl", "transition-colors", action_0.variant === "secondary" ? "bg-gray-100 text-gray-700 active:bg-gray-200" : "bg-gray-900 text-white active:bg-gray-800", "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0")}>{action_0.icon && renderIcon(action_0.icon)}<span>{action_0.label}</span></button>;
      })}</div>;
    $[28] = actions;
    $[29] = hasActions;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t1 || $[32] !== t13 || $[33] !== t14 || $[34] !== t15) {
    t16 = <header className={t1}>{t13}{t14}{t15}</header>;
    $[31] = t1;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
}
function _temp3(tab, index_0) {
  return <button key={index_0} onClick={tab.onClick} className={classNames("flex items-center gap-2 whitespace-nowrap border-b-2 px-3 pb-3 pt-2 text-sm font-semibold transition-colors focus-visible:outline-none", tab.active ? "border-gray-900 text-gray-900" : "border-transparent text-gray-500 hover:text-gray-700 active:text-gray-900")}>{tab.icon}{tab.label}</button>;
}
function _temp2(t0) {
  const variant = t0 === undefined ? "primary" : t0;
  const baseStyles = classNames("inline-flex items-center justify-center gap-2", "h-10 px-4 py-1.5", "whitespace-nowrap text-sm font-semibold", "rounded-xl", "transition-colors duration-150", "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-gray-900", "disabled:pointer-events-none disabled:opacity-50", "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0");
  switch (variant) {
    case "secondary":
      {
        return classNames(baseStyles, "bg-transparent text-gray-600", "hover:bg-gray-100 hover:text-gray-900", "active:bg-gray-200");
      }
    case "primary":
    default:
      {
        return classNames(baseStyles, "bg-white text-gray-900", "border border-gray-200", "shadow-xs", "hover:border-gray-300 hover:bg-gray-50", "active:scale-[0.99] active:shadow-inner");
      }
  }
}
function _temp(icon) {
  if (icon === "plus") {
    return <PlusIcon className="h-4 w-4" />;
  }
  if (icon === "settings") {
    return <Cog6ToothIcon className="h-4 w-4" />;
  }
  if (typeof icon === "object") {
    return icon;
  }
  return null;
}